import { useState, useEffect, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Button } from '../../../shared/components/ui/Button.js'
import { Input } from '../../../shared/components/ui/Input.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { api } from './api.js'
import { ErrorBox } from './ErrorBox.js'
import { maskCNPJ, type EmpresaDaConta, type EmpresaEditFormData } from './types.js'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  empresa: EmpresaDaConta
}

export function EditarEmpresaModal({ open, onClose, onSaved, empresa }: Props) {
  const [form, setForm] = useState<EmpresaEditFormData>({
    nome: '',
    razao_social: '',
    cnpj: '',
    status: 'active',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm({
        nome: empresa.nome,
        razao_social: empresa.razao_social ?? '',
        cnpj: empresa.cnpj ?? '',
        status: empresa.status,
      })
      setError('')
    }
  }, [open, empresa])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) return
    setLoading(true)
    setError('')
    try {
      await api.patch(`/api/v1/super/empresas/${empresa.id}`, {
        nome: form.nome,
        cnpj: form.cnpj || null,
        razao_social: form.razao_social || null,
      })
      onSaved()
      onClose()
      toast.success('Empresa atualizada', { description: form.nome })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro inesperado'
      setError(msg)
      toast.error('Erro ao atualizar empresa', { description: msg })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Editar empresa — ${empresa.nome}`} size="md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
        <Input
          label="Nome da farmácia"
          value={form.nome}
          onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
          autoFocus
        />
        <Input
          label="Razão Social"
          placeholder="Farmácia Saúde Vital LTDA"
          value={form.razao_social}
          onChange={(e) => setForm((f) => ({ ...f, razao_social: e.target.value }))}
        />
        <Input
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          value={form.cnpj}
          onChange={(e) => setForm((f) => ({ ...f, cnpj: maskCNPJ(e.target.value) }))}
          inputMode="numeric"
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-3 mt-2 pt-4 border-t border-[#f1f5f9]">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={loading} disabled={!form.nome.trim()}>
            Salvar alterações
          </Button>
        </div>
      </form>
    </Modal>
  )
}
